// goalAdapter — the `conversationGoals` wire read → the panel's `ChatGoals`
// (parse, don't validate). Lenient per item: a goal, objective, term or part
// that does not parse is dropped rather than failing the whole read; an
// unknown status reads as the most cautious one ("not yet" / "missing"), so a
// shape surprise never claims coverage.

import type {
    ChatGoal,
    ChatGoalsRead,
    ConceptName,
    ConceptObjective,
    EarlierGoal,
    GoalObjective,
    GoalPart,
    GoalRef,
    GoalSource,
    GoalTerm,
    ObjectiveStatus,
    PartLabel,
    Passage,
    Requirement,
    TermStatus,
} from "./AppShellHost/rightPanel/goal.js";
import { fields, list, num, str } from "./wireShape.js";

const each = <A>(
    v: unknown,
    parse: (f: ReadonlyMap<string, unknown>) => A | undefined,
): readonly A[] =>
    list(v).flatMap((item) => {
        const f = fields(item);
        const a = f === undefined ? undefined : parse(f);
        return a === undefined ? [] : [a];
    });

const labelOf = (v: unknown): PartLabel | undefined => {
    const f = fields(v);
    const type = str(f?.get("type"));
    const ordinal = num(f?.get("ordinal"));
    const title = str(f?.get("title"));
    const excerpt = str(f?.get("excerpt"));
    if (type === "question" && ordinal !== undefined) return { type, ordinal };
    if (type === "section" && title !== undefined) return { type, title };
    if (type === "passage" && excerpt !== undefined) return { type, excerpt };
    return undefined;
};

const partOf = (f: ReadonlyMap<string, unknown>): GoalPart | undefined => {
    const rootId = str(f.get("rootId"));
    const materialId = str(f.get("materialId"));
    const elementId = str(f.get("elementId"));
    const label = labelOf(f.get("label"));
    return rootId === undefined ||
        materialId === undefined ||
        elementId === undefined ||
        label === undefined
        ? undefined
        : { rootId, materialId, elementId, label };
};

const passageOf = (v: unknown): Passage | undefined => {
    const f = fields(v);
    const sourceId = str(f?.get("sourceId"));
    const sourceTitle = str(f?.get("sourceTitle"));
    const quote = str(f?.get("quote"));
    return sourceId === undefined ||
        sourceTitle === undefined ||
        quote === undefined
        ? undefined
        : { sourceId, sourceTitle, quote, page: num(f?.get("page")) };
};

const objectiveStatusOf = (v: unknown): ObjectiveStatus =>
    v === "met" ? "met" : v === "partly" ? "partly" : "not_yet";

const termStatusOf = (v: unknown): TermStatus =>
    v === "defined" ? "defined" : v === "used" ? "used" : "missing";

const objectiveOf = (
    f: ReadonlyMap<string, unknown>,
): GoalObjective | undefined => {
    const id = str(f.get("id"));
    const statement = str(f.get("statement"));
    return id === undefined || statement === undefined
        ? undefined
        : {
              id,
              statement,
              from: passageOf(f.get("from")),
              explains: each(f.get("explains"), partOf),
              practises: each(f.get("practises"), partOf),
              status: objectiveStatusOf(f.get("status")),
              suggestion: f.get("suggestion") === true,
          };
};

const conceptNameOf = (
    f: ReadonlyMap<string, unknown>,
): ConceptName | undefined => {
    const conceptId = str(f.get("conceptId"));
    const text = str(f.get("text"));
    return conceptId === undefined || text === undefined
        ? undefined
        : { conceptId, text };
};

const conceptObjectiveOf = (
    f: ReadonlyMap<string, unknown>,
): ConceptObjective | undefined => {
    const id = str(f.get("id"));
    const statement = str(f.get("statement"));
    return id === undefined || statement === undefined
        ? undefined
        : { id, statement };
};

const termOf = (f: ReadonlyMap<string, unknown>): GoalTerm | undefined => {
    const id = str(f.get("id"));
    const conceptId = str(f.get("conceptId"));
    const text = str(f.get("text"));
    const broader = fields(f.get("broader"));
    return id === undefined || conceptId === undefined || text === undefined
        ? undefined
        : {
              id,
              conceptId,
              text,
              definition: str(f.get("definition")),
              definitionClaims: list(f.get("definitionClaims")).flatMap(
                  (c) => str(c) ?? [],
              ),
              definedIn: passageOf(f.get("definedIn")),
              defines: each(f.get("defines"), partOf),
              uses: each(f.get("uses"), partOf),
              status: termStatusOf(f.get("status")),
              broader:
                  broader === undefined ? undefined : conceptNameOf(broader),
              narrower: each(f.get("narrower"), conceptNameOf),
              related: each(f.get("related"), conceptNameOf),
              objectives: each(f.get("objectives"), conceptObjectiveOf),
          };
};

const requirementOf = (
    f: ReadonlyMap<string, unknown>,
): Requirement | undefined => {
    const sourceId = str(f.get("sourceId"));
    const title = str(f.get("title"));
    return sourceId === undefined || title === undefined
        ? undefined
        : { sourceId, title };
};

const sourceOf = (f: ReadonlyMap<string, unknown>): GoalSource | undefined => {
    const sourceId = str(f.get("sourceId"));
    const title = str(f.get("title"));
    return sourceId === undefined || title === undefined
        ? undefined
        : { sourceId, title };
};

const goalRefOf = (v: unknown): GoalRef | undefined => {
    const f = fields(v);
    const contextId = str(f?.get("contextId"));
    const title = str(f?.get("title"));
    return contextId === undefined || title === undefined
        ? undefined
        : { contextId, title };
};

const strings = (v: unknown): readonly string[] =>
    list(v).flatMap((a) => {
        const s = str(a);
        return s === undefined ? [] : [s];
    });

const goalOf = (f: ReadonlyMap<string, unknown>): ChatGoal | undefined => {
    const contextId = str(f.get("contextId"));
    const title = str(f.get("title"));
    return contextId === undefined || title === undefined
        ? undefined
        : {
              contextId,
              title,
              purpose: str(f.get("purpose")),
              audience: strings(f.get("audience")),
              audienceFrom: goalRefOf(f.get("audienceFrom")),
              requirements: each(f.get("requirements"), requirementOf),
              sources: each(f.get("sources"), sourceOf),
              objectives: each(f.get("objectives"), objectiveOf),
              terms: each(f.get("terms"), termOf),
              subGoals: each(f.get("subGoals"), goalOf),
          };
};

// An earlier goal is offered only with at least one objective (the server
// says so too; a zero here is dropped, never shown as an empty goal).
const earlierOf = (
    f: ReadonlyMap<string, unknown>,
): EarlierGoal | undefined => {
    const contextId = str(f.get("contextId"));
    const title = str(f.get("title"));
    const objectiveCount = num(f.get("objectiveCount")) ?? 0;
    return contextId === undefined || title === undefined || objectiveCount < 1
        ? undefined
        : {
              contextId,
              title,
              purpose: str(f.get("purpose")),
              objectiveCount,
              chats: each(f.get("conversations"), (c) => {
                  const id = str(c.get("id"));
                  const title = str(c.get("title"));
                  return id === undefined || title === undefined
                      ? undefined
                      : { id, title };
              }),
          };
};

export const parseChatGoals = (value: unknown): ChatGoalsRead => {
    const f = fields(value);
    return {
        goals: each(f?.get("goals"), goalOf),
        earlier: each(f?.get("earlier"), earlierOf),
    };
};
